import { useMemo } from 'react'
import useSWR from 'swr'
import { staticOptions } from 'src/lib/core/api-client'
import { GetAppsV2Params, getAppsV2 } from 'src/lib/core/api-methods'
import { APP_STORE_CATEGORY_MAP } from 'src/assets/appsIntegrationsConfig'
import { normalizeV2ListItem, v2UnifiedAppItemFromApi } from 'src/lib/utils/appsIntegrationsHelpers'
import {
  AppCategory,
  UnifiedAppItem,
  V2AppCategory,
  V2AppsListItemApi,
  V2CategoryEntry,
  V2ListAppsResponse,
  V2UnifiedAppItem
} from 'src/lib/types/appsIntegrations'

interface AppsV2Response {
  success: boolean
  data: V2ListAppsResponse
}

export const useAppsV2 = (params?: GetAppsV2Params) => {
  const cacheKey = useMemo(
    () => ['/flo-settlement/api/v2/apps', JSON.stringify(params ?? {})],
    [params]
  )

  const { data, isLoading, error, mutate } = useSWR<AppsV2Response>(
    cacheKey,
    () => getAppsV2(params) as unknown as Promise<AppsV2Response>,
    staticOptions
  )

  const items: UnifiedAppItem[] = useMemo(
    () =>
      data?.data?.items?.map(row =>
        normalizeV2ListItem(v2UnifiedAppItemFromApi(row as V2UnifiedAppItem | V2AppsListItemApi))
      ) ?? [],
    [data]
  )

  const popular: UnifiedAppItem[] = useMemo(
    () =>
      data?.data?.popular?.map(row =>
        normalizeV2ListItem(v2UnifiedAppItemFromApi(row as V2UnifiedAppItem | V2AppsListItemApi))
      ) ?? [],
    [data]
  )

  const featured: V2UnifiedAppItem[] = useMemo(
    () =>
      data?.data?.featured?.map(row =>
        v2UnifiedAppItemFromApi(row as V2UnifiedAppItem | V2AppsListItemApi)
      ) ?? [],
    [data]
  )

  const humanize = (enumKey: string) =>
    enumKey
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase())

  const categories: AppCategory[] = useMemo(() => {
    const raw = data?.data?.categories ?? []
    const seen = new Set<string>()
    const out: AppCategory[] = []
    raw.forEach(entry => {
      const enumKey: V2AppCategory =
        typeof entry === 'string' ? entry : ((entry as V2CategoryEntry).category)
      const title = typeof entry === 'string' ? humanize(entry) : (entry as V2CategoryEntry).title
      const subtitle = typeof entry === 'string' ? undefined : (entry as V2CategoryEntry).subtitle
      const unified = APP_STORE_CATEGORY_MAP[enumKey] ?? 'other'
      if (seen.has(unified)) return
      seen.add(unified)
      out.push({ key: unified, label: title, subtitle })
    })
    return out
  }, [data])

  return { items, popular, featured, categories, isLoading, error, mutate }
}
